import type { ArtifactRegistry } from "../artifacts/ArtifactDefinition";
import type { AssetLoader } from "../assets/AssetLoader";
import type { SfxManager } from "../audio/SfxManager";
import { DebugOverlay, isDebugEnabled } from "../debug/DebugOverlay";
import type { InteractionRegistry } from "../interaction/InteractionRegistry";
import type { KnowledgeCatalog } from "../knowledge/KnowledgeGraph";
import type { GameStorage } from "../persistence/GameStorage";
import type { GameClock } from "./GameClock";
import type { Platform } from "../platform/Platform";
import type { PixiRenderer } from "../rendering/PixiRenderer";
import type { SceneImporter } from "../world/import/SceneImporter";
import { createBuiltinActionRegistry, createBuiltinConditionRegistry } from "../world/rules/builtins";
import { RuleEngine } from "../world/rules/RuleEngine";
import { World } from "../world/World";
import { CommandBus } from "./commands/Command";
import { EventBus } from "./events/EventBus";
import type { GameContext } from "./GameContext";
import type { EntityDefinitionRegistry } from "../game-content/EntityDefinitionRegistry";
import type { RecipeRegistry } from "../crafting/RecipeRegistry";

const AUTOSAVE_DEBOUNCE_MS = 300; // ponytail: fixed debounce; make configurable if drag-perf ever needs it

export interface GameEngineOptions {
  readonly renderer: PixiRenderer;
  readonly storage: GameStorage;
  readonly assets: AssetLoader;
  readonly clock: GameClock;
  readonly platform: Platform;
  readonly knowledgeCatalog: KnowledgeCatalog;
  readonly artifacts: ArtifactRegistry;
  readonly interactions: InteractionRegistry;
  readonly entityDefinitions: EntityDefinitionRegistry;
  readonly sceneImporter: SceneImporter<unknown>;
  /** Loads a scene's raw JSON by id. Kept as a seam so native platforms can swap `fetch` for bundled assets. */
  readonly loadSceneJson: (sceneId: string) => Promise<unknown>;
  readonly sfx: SfxManager;
  readonly recipes?: RecipeRegistry;
}

export type SaveStatus = "idle" | "saving" | "saved" | "error";

/** Owns lifecycle: loading worlds/assets, the fixed update loop, pause/resume, save/restore,
 *  and foreground/background transitions. Holds no PixiJS state itself — that's `renderer`'s job. */
export class GameEngine {
  readonly context: GameContext;

  private readonly ruleEngine: RuleEngine;
  private running = false;
  private saveStatus: SaveStatus = "idle";
  private saveTimer: ReturnType<typeof setTimeout> | undefined;
  private debugOverlay: DebugOverlay | undefined;
  private stopTicker: (() => void) | undefined;
  private stopLifecycle: (() => void) | undefined;

  constructor(private readonly options: GameEngineOptions) {
    const world = new World({ knowledgeCatalog: options.knowledgeCatalog, entityDefinitions: options.entityDefinitions });
    const events = new EventBus();
    const commands = new CommandBus(
      { world, events, knowledge: world.knowledge, artifacts: options.artifacts, recipes: options.recipes },
      () => this.scheduleAutosave(),
    );
    this.context = {
      world,
      events,
      commands,
      knowledge: world.knowledge,
      artifacts: options.artifacts,
      interactions: options.interactions,
      sfx: options.sfx,
      recipes: options.recipes,
    };
    this.ruleEngine = new RuleEngine(events, createBuiltinConditionRegistry(), createBuiltinActionRegistry(), world, world.knowledge);
  }

  async loadWorld(sceneId: string): Promise<void> {
    const raw = await this.options.loadSceneJson(sceneId);
    const definition = this.options.sceneImporter.import(raw);
    await this.options.renderer.loadTerrain(definition.terrainFile);
    const terrainObjects = this.options.renderer.terrainObjects;
    const scene = this.context.world.loadScene(definition, terrainObjects);
    await this.options.renderer.loadScene(scene);
    this.ruleEngine.wireSceneSecrets(scene.definition.secrets ?? []);
    this.context.events.emit("scene:entered", { sceneId });
  }

  async start(): Promise<void> {
    this.running = true;
    this.stopLifecycle = this.options.platform.onLifecycleChange((state) => {
      if (state === "background") void this.flushSave();
    });

    if (isDebugEnabled()) {
      this.debugOverlay = new DebugOverlay();
      this.options.renderer.app.stage.addChild(this.debugOverlay.container);
    }

    const tick = () => this.tick();
    this.options.renderer.app.ticker.add(tick);
    this.stopTicker = () => this.options.renderer.app.ticker.remove(tick);
  }

  stop(): void {
    this.stopTicker?.();
    this.stopLifecycle?.();
    this.running = false;
  }

  pause(): void {
    this.running = false;
  }

  resume(): void {
    this.running = true;
  }

  private tick(): void {
    if (!this.running) return;
    const deltaSeconds = this.options.renderer.app.ticker.deltaMS / 1000;
    const scene = this.context.world.getCurrentScene();
    this.options.renderer.update(deltaSeconds, this.context.world);
    this.options.renderer.syncEntities(scene.entities);
    this.debugOverlay?.update(
      {
        fps: this.options.renderer.app.ticker.FPS,
        sceneId: scene.id,
        entityCount: scene.entities.length,
        saveStatus: this.saveStatus,
      },
      this.context.world,
    );
  }

  private scheduleAutosave(): void {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => void this.flushSave(), AUTOSAVE_DEBOUNCE_MS);
  }

  private async flushSave(): Promise<void> {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = undefined;
    }
    this.saveStatus = "saving";
    try {
      await this.options.storage.autoSave(this.context.world.toSave());
      this.saveStatus = "saved";
      this.context.events.emit("save:completed", { at: this.options.clock.now().getTime() });
    } catch (error) {
      this.saveStatus = "error";
      console.error("[persistence] save failed", error);
    }
  }
}
